import React, { useMemo } from 'react';
import { SystemCard } from '../components/SystemCard';
import { SystemCardPopoverContent } from '../components/SystemCardPopoverContent';
import { MonitorWithMetrics } from '../types';
import { iconForEnergySink, isPassiveTechnology } from '../utils';

const MONITORING_SYSTEM_IMAGE = (
  <svg
    width='61'
    height='61'
    viewBox='0 0 61 61'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M7.77452 6.98634H55.335H55.3361C56.1295 6.9841 56.8892 7.30099 57.4489 7.86291C58.0063 8.42485 58.3187 9.1868 58.312 9.97797V46.2284C58.3142 47.0173 57.9996 47.7748 57.4422 48.3344C56.8847 48.8919 56.1273 49.2043 55.3361 49.2043H42.2589V54.556H49.9681C50.9527 54.556 51.7528 55.354 51.7528 56.3384V58.7142V60.4988C51.7528 60.4988 50.4424 60.5011 49.9682 60.4988H13.1424C12.8443 60.5003 12.5407 60.4999 12.1521 60.4994C11.9224 60.4991 11.6631 60.4988 11.3578 60.4988V58.7142V56.3204C11.3578 55.3359 12.158 54.538 13.1425 54.538H20.8517V49.1863H7.77452C6.98335 49.1885 6.22585 48.8738 5.66846 48.3164C5.11103 47.759 4.79634 47.0016 4.7986 46.2126V9.96227C4.79638 9.17109 5.11099 8.41359 5.66846 7.85618C6.22589 7.29877 6.98335 6.98409 7.77452 6.98634ZM52.368 45.0214C53.3525 45.0214 54.1505 44.2212 54.1505 43.2367V12.9291C54.1482 11.9468 53.3502 11.1489 52.368 11.1467H10.7411C9.75887 11.1489 8.96094 11.9469 8.95869 12.9291V43.2367C8.95869 44.2212 9.75666 45.0214 10.7411 45.0214H52.368ZM45.2253 28.7327H48.7961L48.7961 28.7333C49.2951 28.7333 49.7007 29.1221 49.7007 29.6054C49.7007 30.0864 49.2951 30.4752 48.7961 30.4752H44.6716C44.3298 30.4752 44.0153 30.2907 43.8604 29.9985L41.9235 26.315L38.9315 35.8742C38.8222 36.2235 38.4986 36.4695 38.1203 36.4937H38.0611C37.7056 36.4937 37.382 36.2916 37.2384 35.9753L31.5508 23.532L25.8699 35.9927C25.7173 36.3244 25.3641 36.5309 24.9881 36.5089C24.6098 36.4847 24.2885 36.2387 24.1791 35.8916V35.8894L21.1804 26.3302L19.2435 30.0138C19.0908 30.3081 18.7786 30.4926 18.4391 30.4926H14.3146C13.8133 30.4926 13.41 30.1016 13.41 29.6206C13.41 29.1395 13.8133 28.7486 14.3146 28.7486H17.8854L20.5857 23.6043C20.7429 23.2727 21.0983 23.0706 21.4743 23.0969C21.8526 23.1255 22.1716 23.3781 22.2764 23.7273L25.1932 33.1241L30.7054 21.0564C30.7305 20.9993 30.7601 20.9422 30.7942 20.8894C30.9697 20.6544 31.2523 20.5182 31.5508 20.5182C31.8516 20.5182 32.1342 20.6544 32.3096 20.8894C32.3438 20.9378 32.3734 20.9905 32.3985 21.0454L37.9039 33.1238L40.8503 23.7445C40.955 23.404 41.2649 23.158 41.6318 23.125C42.001 23.0965 42.3519 23.2854 42.5182 23.6039L45.2253 28.7327Z'
      fill='currentColor'
    />
  </svg>
);

type MonitoringUnitProps = {
  monitor: MonitorWithMetrics;
  selected: boolean;
  count?: number;
  isLoading?: boolean;
  readOnly?: boolean;
  actions: {
    onAdd?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    onRemove?: () => void;
    onChangeCount?: (newCount: number) => void;
  };
};

export const MonitoringUnit: React.FC<MonitoringUnitProps> = React.memo(
  ({ monitor, selected, count, isLoading, actions, readOnly }) => {
    const EnergyIcon = iconForEnergySink(monitor.energySink);

    const icons = useMemo(
      () => ({
        EnergyIcon: EnergyIcon,
        energyIconCount: monitor.relativeEnergyDemand,
        costIconCount: monitor.relativeCost,
      }),
      [EnergyIcon, monitor.relativeEnergyDemand, monitor.relativeCost],
    );

    const hoverPopover = useMemo(
      () => (
        <SystemCardPopoverContent
          name={monitor.name}
          energySource={monitor.energySink}
          EnergySourceIcon={EnergyIcon}
          limit={monitor.limit}
          coveragePercentage={monitor.coveragePercentage}
          energyDemand={Number(monitor.energyUsePerYr)}
          capitalCost={Number(monitor.capitalCostPerUnit)}
          annualCost={Number(monitor.maintenanceCostPerUnit)}
          coverageFieldTitle={'monitor'}
          designLife={monitor.designLife}
          isLoading={isLoading}
        />
      ),
      [monitor, EnergyIcon, isLoading],
    );

    return (
      <SystemCard
        actions={actions}
        count={count}
        limit={monitor.limit}
        name={monitor.name}
        selected={selected}
        systemImage={MONITORING_SYSTEM_IMAGE}
        icons={icons}
        hoverPopover={hoverPopover}
        readOnly={readOnly}
        unitsFixed={isPassiveTechnology(monitor)}
      />
    );
  },
);

MonitoringUnit.displayName = 'MonitoringUnit';
